// future value of ₹1 invested at the end of every month, with the SIP growing by step_up_rate % each year
// the annual return is converted to the effective monthly return, step_up_rate 0 is a flat SIP
const _getSipFactor = (annual_return, years, step_up_rate) => {
    const annual_rate = (Number(annual_return) || 0) / 100;
    const monthly_rate = Math.pow(1 + annual_rate, 1 / 12) - 1;
    const yearly_step_up = 1 + (Number(step_up_rate) || 0) / 100;

    let year_factor = 12;
    if (monthly_rate !== 0) {
        year_factor = (Math.pow(1 + monthly_rate, 12) - 1) / monthly_rate;
    }

    let factor = 0;
    for (let year = 0; year < years; year++) {
        const months_left = (years - year - 1) * 12;
        factor += Math.pow(yearly_step_up, year) * year_factor * Math.pow(1 + monthly_rate, months_left);
    }
    return factor;
};

// corpus the monthly SIP grows to in the given years
export const _getSipCorpus = (monthly_sip, annual_return, years, step_up_rate) => {
    return Math.round((Number(monthly_sip) || 0) * _getSipFactor(annual_return, years, step_up_rate));
};

// corpus the current savings grow to in the given years
export const _getSavingsCorpus = (current_savings, annual_return, years) => {
    return Math.round((Number(current_savings) || 0) * Math.pow(1 + (Number(annual_return) || 0) / 100, years));
};

// monthly SIP (the first year SIP when stepping up) needed to reach the target corpus along with the current savings
export const _getRequiredSip = (target_corpus, current_savings, annual_return, years, step_up_rate) => {
    const factor = _getSipFactor(annual_return, years, step_up_rate);
    if (factor === 0) {
        return 0;
    }

    const corpus_needed_from_sip = Math.max(0, target_corpus - _getSavingsCorpus(current_savings, annual_return, years));
    return Math.ceil(corpus_needed_from_sip / factor);
};

// monthly SIP needed to reach the target amount along with the amount saved already, used by the dream home and kids education goals
// the monthly return is the annual return (in %) / 12, the SIP is invested at the start of every month and rounded up to the next 100
export const _getGoalRequiredSip = (target_amount, saved_amount, annual_return, years) => {
    const months = years * 12;
    if (months <= 0) {
        return 0;
    }

    const amount_needed_from_sip = Math.max(0, target_amount - _getSavingsCorpus(saved_amount, annual_return, years));
    if (amount_needed_from_sip === 0) {
        return 0;
    }

    const monthly_rate = (Number(annual_return) || 0) / 100 / 12;
    let sip = amount_needed_from_sip / months;
    if (monthly_rate !== 0) {
        sip = amount_needed_from_sip * monthly_rate / ((Math.pow(1 + monthly_rate, months) - 1) * (1 + monthly_rate));
    }

    return Math.ceil(sip / 100) * 100;
};

// net value at the end year of the planned one time investments (+) and withdrawals (-), each grown from its year to the end year
export const _getPlannedChangesCorpus = (planned_changes, annual_return, end_year) => {
    const annual_rate = (Number(annual_return) || 0) / 100;

    let corpus = 0;
    planned_changes.forEach((change) => {
        const years_left = Math.max(end_year - change.year, 0);
        const grown_amount = (Number(change.amount) || 0) * Math.pow(1 + annual_rate, years_left);
        corpus += change.type === "withdraw" ? -grown_amount : grown_amount;
    });

    return Math.round(corpus);
};
